import { z } from 'zod';
import type { ProjectIntent } from './siteRag';

const id = z.string().min(1).max(160).regex(/^[A-Za-z0-9_.:-]+$/);
const hash = z.string().regex(/^[a-f\d]{64}$/);
const mw = z.number().finite().positive().max(1_000_000_000).nullable();
const date = z.string().regex(/^\d{4}-\d{2}-\d{2}$/).refine(value => {
  const parsed = new Date(`${value}T00:00:00Z`);
  return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}).nullable();

/** An explicit edit to shared business inputs. No result, source adoption,
 * equipment rating, standby duty, utility capacity or promotion is accepted. */
export const generationProjectInputChangesSchema = z.object({
  phaseId: id.optional(),
  loadMW: mw.optional(),
  loadBasis: z.enum(['facility', 'it-load']).nullable().optional(),
  operationTargetDate: date.optional(),
  pue: z.number().finite().min(1).max(10).nullable().optional(),
  workloadProfile: z.enum(['ai-training', 'ai-inference', 'hpc', 'enterprise', 'colocation', 'edge', 'mixed']).nullable().optional(),
  coolingStrategy: z.enum(['air', 'liquid', 'hybrid', 'undecided']).optional(),
  heatRejection: z.enum(['dry-cooler', 'wet-cooling-tower', 'hybrid', 'once-through', 'absorption-cchp', 'free-cooling', 'unknown']).optional(),
  gridConnection: z.enum(['islanded', 'grid-connected', 'undecided']).optional(),
  powerComponents: z.array(z.enum(['recip', 'gas-turbine', 'fuel-cell', 'battery', 'grid', 'solar-bess', 'wind-bess', 'smr', 'large-nuclear'])).max(9).optional(),
}).strict().superRefine((changes, context) => {
  if (!Object.keys(changes).some(key => key !== 'phaseId')) context.addIssue({ code: z.ZodIssueCode.custom, message: 'An explicit shared input edit is required.' });
  if (changes.powerComponents && new Set(changes.powerComponents).size !== changes.powerComponents.length) context.addIssue({ code: z.ZodIssueCode.custom, path: ['powerComponents'], message: 'Duplicate power components.' });
  if ((changes.gridConnection != null || changes.powerComponents != null || changes.operationTargetDate !== undefined) && !changes.phaseId) context.addIssue({ code: z.ZodIssueCode.custom, path: ['phaseId'], message: 'Select the exact project phase for this edit.' });
});

export const generationProjectInputEditScopeSchema = z.object({
  kind: z.enum(['generation-impact-v1', 'grid-led-standby-planning']),
  assessmentId: id,
  inputHash: hash,
  resultHash: hash,
}).strict().nullable();

export const syncGenerationProjectInputsRequestSchema = z.object({
  siteId: id, ownerUid: id,
  expectedDecisionHash: hash,
  expectedStudyAreaSelectionHash: hash.nullable(),
  /** Null is the explicit main project-input draft, never an inactive saved comparison. */
  editingAssessment: generationProjectInputEditScopeSchema,
  changes: generationProjectInputChangesSchema,
}).strict();
export type GenerationProjectInputChanges = z.infer<typeof generationProjectInputChangesSchema>;
export type GenerationProjectInputEditScope = z.infer<typeof generationProjectInputEditScopeSchema>;
export type SyncGenerationProjectInputsRequest = z.infer<typeof syncGenerationProjectInputsRequestSchema>;
export interface SyncGenerationProjectInputsResult {
  siteId: string;
  ownerUid: string;
  projectIntent: ProjectIntent;
  decisionHash: string;
  sharedInputHash: string;
  changed: boolean;
  updatedAt: string | null;
  generationProjectInputBinding?: GenerationProjectInputBinding;
}

/** Server-only applicability sidecar; never changes historical solve bytes. */
export interface GenerationProjectInputBinding {
  schemaVersion: 1;
  kind: 'generation-impact-v1';
  assessmentId: string;
  inputHash: string;
  resultHash: string;
  sharedInputHash: string;
}
export const generationProjectInputBindingSchema = z.object({ schemaVersion: z.literal(1),
  kind: z.literal('generation-impact-v1'), assessmentId: id, inputHash: hash,
  resultHash: hash, sharedInputHash: hash }).strict();
